'use client';

import { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useBlogs } from "@/hooks/use-blogs";
import { Search, Calendar, ArrowRight, X } from "lucide-react";
import { Blog, BlogsResponse } from "@/types/blogs";
import { capitalize, formatDate } from "@/lib/utils";
import CallToActionBlock from "@/components/marketing/callToActionBlock";

const PALETTE = [
  { badge: "bg-teal-50 text-brand-teal",     dot: "bg-brand-teal",   accent: "#008080" },
  { badge: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500",  accent: "#10b981" },
  { badge: "bg-orange-50 text-orange-700",   dot: "bg-orange-400",   accent: "#f97316" },
  { badge: "bg-purple-50 text-purple-700",   dot: "bg-purple-500",   accent: "#9845ff" },
  { badge: "bg-blue-50 text-blue-700",       dot: "bg-blue-500",     accent: "#3b82f6" },
  { badge: "bg-rose-50 text-rose-700",       dot: "bg-rose-400",     accent: "#f43f5e" },
];

const CARDS_PER_CATEGORY = 4; // fallback for any category not listed below
const CARDS_PER_CATEGORY_MAP: Record<string, number> = {
  comparison: 5,
  remittance: 5,
  esim: 5,
  news: 3,
  tutorial: 3,
};
const VIEW_ALL_THRESHOLD = 5;

function getCardsPerCategory(category: string) {
  return CARDS_PER_CATEGORY_MAP[category?.toLowerCase()] ?? CARDS_PER_CATEGORY;
}

function buildStyleMap(categories: string[]) {
  const map = new Map<string, (typeof PALETTE)[number]>();
  categories.forEach((cat, i) => map.set(cat, PALETTE[i % PALETTE.length]));
  return map;
}

function BlogCard({
  blog,
  index,
  styleMap,
  first = false,
}: {
  blog: Blog;
  index: number;
  styleMap: Map<string, (typeof PALETTE)[number]>;
  first?: boolean;
}) {
  const style = styleMap.get(blog.category) ?? PALETTE[0];

  return (
    <motion.article
      initial={index === 0 ? false : { y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.06 }}
      className={`h-full md:min-h-[345px] ${first ? "sm:col-span-2 xl:col-span-2" : ""}`}
    >
      <Link
        href={`/blog/${blog.slug}`}
        className={`group flex h-full overflow-hidden global-border-radius bg-white border border-brand-grey/30 hover:border-brand-grey/50 hover:shadow-lg hover:shadow-black/5 transition-all duration-300 ${
          first ? "flex-col md:flex-row" : "flex-col"
        }`}
      >
        {blog.featured_image && (
          <div
            className={`relative overflow-hidden flex-shrink-0 ${
              first ? "h-56 w-full md:h-auto md:w-[46%]" : "h-44 w-full"
            }`}
          >
            <Image
              src={blog.featured_image}
              alt={blog.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
              quality={first ? 75 : 65}
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
          </div>
        )}

        <div className={`flex flex-col flex-1 p-6 gap-4 ${first ? "md:justify-center md:p-8" : ""}`}>
          <span className={`inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-xs font-semibold 3xl:text-sm ${style.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
            {capitalize(blog.category)}
          </span>

          <h2 className={`text-brand-black font-semibold leading-snug ${first ? "text-xl md:text-2xl" : "text-base md:text-lg 3xl:text-xl"}`}>
            {blog.title}
          </h2>

          {/*blog.excerpt && (
            <p className={`text-brand-muted leading-relaxed flex-1 ${first ? "text-base line-clamp-4" : "text-sm 3xl:text-base line-clamp-3"}`}>
              {blog.excerpt}
            </p>
          )*/}

          <div className="flex items-center justify-between pt-4 border-t border-brand-grey/25 mt-auto">
            <span className="flex items-center gap-1.5 text-sm 3xl:text-base text-brand-muted-light">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(blog.published_at)}
            </span>
            <p className="inline-flex items-center gap-1 text-sm 3xl:text-base text-brand-teal group-hover:gap-2 transition-all duration-200">
              Read more <ArrowRight className="h-3 w-3" />
            </p>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

export default function BlogListContent({
  initialBlogs,
  initialCategories,
}: {
  initialBlogs: Blog[];
  initialCategories: string[];
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const styleMap = useMemo(() => buildStyleMap(initialCategories), [initialCategories]);
  const hasFilters = !!debouncedSearch || activeCategory !== "All";

  const groupedByCategory = useMemo(() => {
    const map = new Map<string, Blog[]>();
    initialCategories.forEach((cat) => map.set(cat, []));
    initialBlogs.forEach((blog) => {
      const key =
        initialCategories.find(
          (cat) => cat.toLowerCase() === blog.category?.toLowerCase(),
        ) ?? blog.category;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(blog);
    });
    return map;
  }, [initialBlogs, initialCategories]);

  const { data: blogsData, isPlaceholderData } = useBlogs(
    hasFilters
      ? {
          search: debouncedSearch || undefined,
          category: activeCategory !== "All" ? activeCategory : undefined,
        }
      : undefined,
  );

  const isFiltering = hasFilters && isPlaceholderData;

  const blogs: Blog[] = useMemo(() => {
    if (!hasFilters) return initialBlogs;
    return (blogsData as BlogsResponse | undefined)?.data ?? [];
  }, [hasFilters, initialBlogs, blogsData]);

  const clearFilters = () => {
    setSearch("");
    setActiveCategory("All");
  };

  return (
    <div className="min-h-screen bg-brand-white">
      {/* ── Hero ── */}
      <section className="relative block-title-padding text-center overflow-hidden">
        <div className="relative z-10 lg:max-w-4xl xl:max-w-6xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="page-title"
          >
            Blog
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="page-subtitle"
          >
            Practical guides on international payments and travel eSIM.
          </motion.p>
        </div>
      </section>

      {/* ── Filters ── */}
      <section ref={topRef} className="scroll-mt-24 w-full sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl mx-auto px-4 lg:px-0">
        <div className="flex justify-start sm:justify-center flex-row gap-4 mb-12 x overflow-x-auto gap-2">
          {/*<div className="relative flex-1 min-w-[220px] sm:max-w-xs">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted pointer-events-none" />
            <input
              type="text"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full global-border-radius bg-brand-card border border-brand-stroke/40 pl-10 pr-4 py-3 text-sm text-brand-text placeholder:text-brand-muted focus:outline-none focus:border-brand-teal transition-all duration-200"
            />
          </div> */}

          <div className="flex gap-2 items-center justify-start -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {["All", ...initialCategories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`global-border-radius px-4 py-3 text-sm 3xl:text-base font-medium transition-all duration-200 border whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-brand-black text-brand-white border-stroke-black"
                    : "bg-brand-white-light/80 text-brand-muted-light border-brand-grey hover:bg-brand-white-light hover:border-brand-grey hover:text-brand-black"
                }`}
              >
                {cat === "All" ? "All" : capitalize(cat)}
              </button>
            ))}

            <AnimatePresence>
              {hasFilters && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 global-border-radius border border-brand-grey bg-brand-surface px-4 py-3 text-sm font-medium bg-brand-white-light/60 text-brand-muted-light border-brand-brand-grey hover:bg-brand-white-light hover:border-brand-grey hover:text-brand-black transition-all duration-200"
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── Blog Grid ── */}
      <section className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto pb-24 px-4 lg:px-0">
        <AnimatePresence mode="wait">
          {hasFilters ? (
            isFiltering ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-64 global-border-radius bg-brand-stroke/20 animate-pulse ${
                      i === 0 ? "sm:col-span-2 xl:col-span-2" : ""
                    }`}
                  />
                ))}
              </motion.div>
            ):  blogs.length > 0 ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                {blogs.map((blog: Blog, idx: number) => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    index={idx}
                    styleMap={styleMap}
                    first={idx === 0}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center text-center py-24 global-border-radius border border-brand-stroke/30 bg-brand-surface/50"
              >
                <div className="h-16 w-16 rounded-full bg-brand-card border border-brand-stroke/30 shadow-sm flex items-center justify-center mb-4">
                  <Search className="h-7 w-7 text-brand-muted" />
                </div>
                <p className="text-brand-text font-semibold text-lg mb-2">No articles found</p>
                <p className="text-brand-light-muted text-sm mb-6 max-w-xs">
                  Try adjusting your search or browse all categories.
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-stroke/50 bg-brand-grey px-6 py-2.5 text-sm font-semibold text-brand-card hover:border-brand-teal transition-all duration-200"
                >
                  Clear filters
                </button>
              </motion.div>
            )
          ) : (
            <motion.div
              key="sections"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-16"
            >
              {initialCategories.map((cat) => {
                const catBlogs = groupedByCategory.get(cat) ?? [];
                if (catBlogs.length === 0) return null;
                const cardCount = getCardsPerCategory(cat);

                return (
                  <div key={cat} className="mb-14 md:mb-20">
                    <div className="flex items-baseline justify-center mb-6">
                      <h2 className="text-3xl xs:text-4xl md:text-5xl font-bold text-brand-black mb-0 xs:mb-4">
                        {capitalize(cat)}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {catBlogs.slice(0, cardCount).map((blog, idx) => (
                        <BlogCard
                          key={blog.id}
                          blog={blog}
                          index={idx}
                          styleMap={styleMap}
                          first={idx === 0}
                        />
                      ))}
                    </div>
                    {catBlogs.length > VIEW_ALL_THRESHOLD && (
                      <div className="flex justify-center mt-12">
        
                          <button
                            onClick={() => {
                              setActiveCategory(cat);
                              topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            className="group inline-flex w-full justify-center sm:w-auto items-center button button button-lg"
                          >
                            View More
                          </button>
                    
                      </div>  
                    )}
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {blogs.length > 0 && (
          <div className="mt-12 sm:mt-24">
            <CallToActionBlock 
              smallText="International payments &amp; travel eSIM"
              title="One app for global payments"
              subtitle="Send payments internationally with competitive rates, or stay connected abroad with Vell Travel eSIM — all in one place.."
              buttonText="Get started for free"
              href="/register"
              glow="teal"     
            />
          </div>
        )}
      </section>
    </div>
  );
}